import { QueryClient, useQuery, UseQueryResult } from '@tanstack/react-query';
import {
    SIMULATION_ENDPOINT_PREFIX,
    SimulationTypeEnum,
} from 'routes/HumanResourcesModule/Simulation/helpers/constants';
import { RQAPICall } from 'store/APIClient';
import { SimulationDateString } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { ObsoleteHypotheses } from '../types';

const OBSOLETE_HYPOTHESES_API_KEY = 'obsolete_hypotheses';

/**
 * Resets the query for fetching obsolete hypotheses based on the simulationId.
 *
 * Obsolete Hypotheses: In this context, these are assumptions or data points
 * present in the original simulation that will not be duplicated when creating
 * a new simulation. They become obsolete because they are tied to services
 * not present in the new simulation.
 * @param queryClient - Instance of React Query's QueryClient.
 * @param simulationId - The ID of the simulation for which to reset the query.
 */
export const resetObsoleteHypothesesQuery = (
    queryClient: QueryClient,
    simulationId: number,
) => {
    queryClient.resetQueries({
        queryKey: [OBSOLETE_HYPOTHESES_API_KEY, simulationId],
    });
};

/**
 * Custom hook to fetch obsolete hypotheses for a given simulation.
 *
 * @param simulationId - The ID of the simulation for which obsolete hypotheses are being fetched.
 * @param orgChartHrsSoftwareIds - An array of organization chart HR software IDs to filter the hypotheses.
 * @param isEtablissementsFilterActive - A boolean indicating if the "etablissements" filter is active.
 * @param etablissementSoftwareIds - An array of software IDs for "etablissements" to filter the hypotheses.
 * @param isPayBudgetFilterActive - A boolean indicating if the pay budget filter is active.
 * @param payBudgetSoftwareIds - An array of software IDs for pay budgets to filter the hypotheses.
 * @param simulationType - The type of the simulation, represented as a value from the `SimulationTypeEnum`.
 * @param recurrentInitialisationStartDate - The start date for recurrent initialization, formatted as a `SimulationDateString`.
 * @returns A `UseQueryResult` containing the fetched `ObsoleteHypotheses` data.
 */
export const useGetObsoleteHypotheses = (
    simulationId: number,
    orgChartHrsSoftwareIds: string[],
    isEtablissementsFilterActive: boolean,
    etablissementSoftwareIds: string[],
    isPayBudgetFilterActive: boolean,
    payBudgetSoftwareIds: string[],
    simulationType: SimulationTypeEnum,
    recurrentInitialisationStartDate: SimulationDateString,
): UseQueryResult<ObsoleteHypotheses> => {
    const endpoint = `${SIMULATION_ENDPOINT_PREFIX}/obsolete_hypotheses/${simulationId}`;

    return useQuery({
        queryKey: [OBSOLETE_HYPOTHESES_API_KEY, simulationId],
        queryFn: async () => {
            const getResponse = await RQAPICall({
                endpoint,
                method: 'POST',
                body: {
                    orgChartHrsSoftwareIds,
                    etablissementSoftwareIds,
                    isEtablissementsFilterActive,
                    isPayBudgetFilterActive,
                    payBudgetSoftwareIds,
                    simulationType,
                    recurrentInitialisationStartDate,
                },
            });
            return getResponse.content.data;
        },
        meta: {
            useGlobalOnError: true,
        },
    });
};
